<article>
  <header class="banner" id="main-content-focus" tabindex="-1">
    <div class="layout-grid" id="aria_page_header">
      <h1 appInlineEdit="title"
        [inlineEditValue]="title || ''"
        [inlineEditEnabled]="editMode"
        [inlineEditConfig]="{ type: 'text', placeholder: 'Enter title...' }"
        (inlineEditChange)="editChange.emit($event)"
        [tabindex]="editMode ? 0 : -1"
        [class.inline-editable-error]="titleError">
        @if (title) {
          <span>{{ title }}</span>
        }
        @if (!title && editMode) {
          <span class="placeholder-text">Click to add title...</span>
        }
      </h1>
      @if (subtitle || editMode) {
        <h2
          appInlineEdit="subtitle"
          [inlineEditValue]="subtitle || ''"
          [inlineEditEnabled]="editMode"
          [inlineEditConfig]="{ type: 'text', placeholder: 'Enter subtitle...' }"
          (inlineEditChange)="editChange.emit($event)"
          [tabindex]="editMode ? 0 : -1">
          @if (subtitle) {
            <span>{{ subtitle }}</span>
          }
          @if (!subtitle && editMode) {
            <span class="placeholder-text">Click to add (optional) subtitle...</span>
          }
        </h2>
      }

      <div class="welcome-view__banner-text gcr-welcome"
        appInlineEdit="homeBannerContent"
        [inlineEditValue]="homeBannerContent || ''"
        [inlineEditEnabled]="editMode"
        [inlineEditConfig]="{ type: 'wysiwyg', placeholder: 'Enter banner content...', allowLists: false }"
        (inlineEditChange)="editChange.emit($event)"
        [tabindex]="editMode ? 0 : -1"
        [innerHTML]="homeBannerContent">
      </div>
    </div>
  </header>

  <nav class="welcome-view__cards" aria-label="Search types">
    <ul class="welcome-view__card-list layout-grid layout-grid--row-gap">
      @for (system of menuItems; track system; let i = $index) {
        <li [class.edit-mode]="editMode" [attr.data-guide]="i === 0 ? 'home-services' : null">
          <!-- Edit mode controls -->
          @if (editMode) {
            <app-edit-controls
              customClass="menu-item-controls"
              [showDragHandle]="false"
              [deleteTitle]="system.menuItemId ? ('Deactivate menu item: ' + system.label) : ('Delete menu item: ' + system.label)"
              [deleteIconName]="system.menuItemId ? 'visibility_off' : 'delete'"
              [showLinkIcon]="true"
              [linkIconTitle]="'Link to page: ' + getPageLabel(system.pageId)"
              [linkEditField]="'menuItems[' + i + '].pageId'"
              [linkEditValue]="getPageIdAsString(system.pageId)"
              [linkEditEnabled]="editMode"
              [linkEditConfig]="{ type: 'dropdown', placeholder: 'Select a page...', options: pageDropdownOptions }"
              [linkEditError]="menuItemPageIdErrors[i]"
              (linkEditChange)="editChange.emit({field: $event.field, value: $event.value, index: i})"
              (deleteClick)="editChange.emit({field: 'deleteMenuItem',
                index: i})"></app-edit-controls>
          }
          <!-- Non-edit mode: link wrapper -->
          @if (!editMode) {
            @let underMaintenance = isPageUnderMaintenance(system.pageId);
            <!-- A flagged card needs role AND tabindex to reach the accessibility tree as
            disabled: with no routerLink the anchor has no href and therefore no link role, and an
            unfocusable node collapses to loose static text carrying no disabled state at all. The
            notice supplies the reason, which the greyed treatment alone cannot. -->
            <a
              class="action-card mat-focus-indicator"
              matRipple
              [class.action-card--disabled]="underMaintenance"
              [attr.aria-disabled]="underMaintenance ? 'true' : null"
              [attr.role]="underMaintenance ? 'link' : null"
              [attr.tabindex]="underMaintenance ? 0 : null"
              [routerLink]="preview || underMaintenance ? null : ['/search/' + system.pageId]">
              <mat-card appearance="outlined">
                <mat-card-title>{{ system.label }}</mat-card-title>
                @if (system.description) {
                  <mat-card-content>{{ system.description }}</mat-card-content>
                }
                @if (underMaintenance) {
                  <mat-card-content>{{ siteMetadata.maintenanceNotice }}</mat-card-content>
                }
              </mat-card>
            </a>
          }
          <!-- Edit mode: editable card without link wrapper. It carries .action-card so the
          shared utility styles it exactly as the anchor it stands in for - the editor is the
          preview of the published page, so the two must not drift. -->
          @if (editMode) {
            <div class="action-card">
              <mat-card appearance="outlined" class="actionable">
                <mat-card-title [appInlineEdit]="'menuItems[' + i + '].label'"
                  [inlineEditValue]="system.label || ''"
                  [inlineEditEnabled]="editMode"
                  [inlineEditConfig]="{ type: 'text', placeholder: 'Enter menu label...' }"
                  (inlineEditChange)="editChange.emit({field: $event.field, value: $event.value, index: i})"
                  [class.inline-editable-error]="menuItemLabelErrors[i]"
                  tabindex="0">
                  @if (system.label) {
                    <span>{{ system.label }}</span>
                  }
                  @if (!system.label && editMode) {
                    <span class="placeholder-text">Click to add menu label...</span>
                  }
                </mat-card-title>
                <mat-card-content [appInlineEdit]="'menuItems[' + i + '].description'"
                  [inlineEditValue]="system.description || ''"
                  [inlineEditEnabled]="editMode"
                  [inlineEditConfig]="{ type: 'textarea', placeholder: 'Enter menu description...' }"
                  (inlineEditChange)="editChange.emit({field: $event.field, value: $event.value, index: i})"
                  tabindex="0">
                  @if (system.description) {
                    <span>{{ system.description }}</span>
                  }
                  @if (!system.description && editMode) {
                    <span class="placeholder-text">Click to add (optional) description...</span>
                  }
                </mat-card-content>
              </mat-card>
            </div>
          }
        </li>
      }
    </ul>
  </nav>

  <!-- Menu Item Action Buttons -->
  @if (editMode) {
    <div class="preview-edit-block">
      <button matButton="filled" (click)="editChange.emit({field: 'addMenuItem'})">
        <mat-icon>add</mat-icon>
        Add Menu Item
      </button>
      <button matButton="tonal"
        [matMenuTriggerFor]="inactiveItemsMenu"
        [disabled]="!inactiveMenuItems || inactiveMenuItems.length === 0">
        <mat-icon>visibility_off</mat-icon>
        Manage Inactive Items
      </button>
      <mat-menu #inactiveItemsMenu="matMenu">
        @for (item of inactiveMenuItems; track item; let i = $index) {
          <button mat-menu-item
            (click)="editChange.emit({field: 'reactivateMenuItem', index: i})">
            <mat-icon>visibility</mat-icon>
            <span>{{ item.label }}</span>
          </button>
        }
        @if (!inactiveMenuItems || inactiveMenuItems.length === 0) {
          <div
            mat-menu-item
            disabled>
            No inactive items
          </div>
        }
      </mat-menu>
    </div>
  }

  <div class="welcome-view__sign-in-prompt layout-grid">
    @if ((preview && authenticationEnabled) || (authenticationEnabled && authService && !authService.isSignedIn)) {
      <p>
        Do you have an account? Sign in to save time.
      </p>
    }

    @if ((preview && authenticationEnabled) || (authenticationEnabled && authService && !authService.isSignedIn)) {
      <a matButton [routerLink]="!preview && !editMode ? '/sign-in' : null">
        <mat-icon>login</mat-icon>
        <span>Sign In</span>
      </a>
    }
  </div>
</article>
